/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import {
    Component,
    Type,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { IClientLogFilter } from 'generated-types';
import { ClientLogFilter } from 'object-types';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';

import {
    IpAddrMatchConfigItem,
} from 'ajs/modules/match/factories/ip-addr-match.config-item.factory';

import { PathMatchConfigItem } from 'ajs/modules/match/factories/path-match.config-item.factory';
import * as l10n from './client-log-filters.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * List of matches supported in Client Log Filter modal.
 */
const supportedMatches = ['client_ip', 'uri'];

/**
 * Partial type of Client Log Filter.
 */
type ClientLogFilterPartial = Omit<IClientLogFilter, 'client_ip' | 'uri'>;

/**
 * Client Log Filter Config type.
 */
interface IClientLogFilterConfig extends ClientLogFilterPartial {
    client_ip?: IpAddrMatchConfigItem;
    uri?: PathMatchConfigItem;
}

/**
 * @description
 *
 *   Client Log Filters Message Item.
 *
 * @author Sarthak kapoor
 */
export class ClientLogFilterConfigItem extends
    withEditChildMessageItemMixin<IClientLogFilterConfig, typeof MessageItem>(
        withFullModalMixin(MessageItem),
    )<IClientLogFilterConfig> {
    public static ngDependencies = ['l10nService'];

    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: ClientLogFilter,
            windowElement: 'lazy-load',
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getNgDependency('l10nService');
        this.l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Enable or Deactivate the selected filter.
     * The operation type depends upon the button clicked.
     */
    public setEnabled(enable = true): void {
        this.config.enabled = enable;
    }

    /**
     * Return the count of matches.
     */
    public get matchCount(): number {
        return supportedMatches.filter(match => this.hasMatchByField(match)).length;
    }

    /**
     * @override
     * Return true if the match exists in the config.
     */
    public hasMatchByField(matchFieldName: string): boolean {
        return Boolean(this.config[matchFieldName]);
    }

    /**
     * @override
     * Return if there are any matches present.
     */
    public hasAnyEntry(): boolean {
        return this.matchCount > 0;
    }

    /**
     * @override
     * Add a new match entry based on the fieldname passed.
     */
    public addMatch(entryFieldName: string): void {
        this.safeSetNewChildByField(entryFieldName);
    }

    /**
     * Import lazy loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const {
            VsClientLogFilterModalComponent,
        } = await import(
            /* webpackChunkName: "virtual-service-modal" */
            /* eslint-disable-next-line max-len */
            'ng/lazy-loaded-components/modals/virtual-service-modal/vs-analytics-section/vs-client-log-filter-modal/vs-client-log-filter-modal.component'
        );

        return VsClientLogFilterModalComponent as Type<Component>;
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.clientLogFilterBreadcrumbTitle);
    }
}
